<template lang="html">
  <div
    class="selection-container start-xs"
    :class="{ 'live-font': activityType === 'live' }"
  >
    <div class="pl-subheading">
      Assessment
      <i
        v-my-tooltip="assessment_description"
        class="fa fa-info-circle"
        aria-hidden="true"
      />
    </div>
    <!-- Assessment Radio Selection Buttons -->
    <div class="radio-button-container">
      <div class="row" style="margin: 10px 10px 10px 0px">
        <pl-radio v-model="assessment_status" :value="false" />
        <span>No Assessment</span>
      </div>
      <div class="row" style="margin: 10px 10px 10px 0px">
        <pl-radio v-model="assessment_status" :value="true" />
        <span>Teacher can provide assessment with points</span>
      </div>
    </div>
    <!-- Point selection and customization section. Only Visible when grading is enabled -->
    <!-- Criteria Scale is not available in the live discussion -->
    <div class="italics pl-caption ma4" v-if="getCurrentRT.is_sandbox">
      Assessment settings can be managed in your RoundTable, however these
      options are locked for this demo.
    </div>
    <div v-if="assessment_status" :class="{ hide: getCurrentRT.is_sandbox }">
      <!-- Scale Selection Container -->
      <div
        style="padding-top: 25px"
        class="pl-subheading"
        v-if="activityType === 'written'"
      >
        Max Score

        <toggle-button
          @change="toggleCriteriaScale"
          :value="!!getCurrentRT.point_cap"
          :labels="false"
          color="#72ccd7"
          class="mh3"
        />

        <i
          v-my-tooltip="scale_description"
          class="fa fa-info-circle"
          aria-hidden="true"
          v-if="!getCurrentRT.is_sandbox"
        />
        <!-- Scale selection container -->
        <div
          v-if="point_scale !== null"
          class="mv3"
          style="margin-left: 6px; display: flex"
        >
          <pl-button
            class="pl-icon"
            style="display: inline-block"
            @click.native="point_scale -= 1"
            :disabled="Number(point_scale) === 0"
          >
            <i class="chevron clickable fa fa-chevron-left" />
          </pl-button>
          <pl-input
            @input="inputed"
            v-model="point_scale"
            :centeredText="true"
            :error="point_scale === ''"
            style="
              width: 50px;
              height: 23px;
              text-align: center;
              display: inline-block;
            "
          >
            {{ point_scale }}
          </pl-input>
          <pl-button
            class="pl-icon"
            style="display: inline-block"
            @click.native="addPoint"
          >
            <i class="chevron clickable fa fa-chevron-right" />
          </pl-button>
        </div>
      </div>

      <!-- Points Selection Container -->
      <div class="point-selection-container">
        <div class="pl-subheading">Criteria</div>
        <!-- Tabe Descriptions Header -->
        <div
          class="individual-point-selection row middle-xs around-xs no-margin"
          style="color: #808284; font-size: 12px"
        >
          <div class="col-xs-1"></div>
          <div class="no-padding point-type-text col-xs-3">Title</div>
          <div class="col-xs-8">Description</div>
        </div>

        <!-- List of Available Points to Teacher -->
        <div class="individual-points-container">
          <div
            :key="`${index}-${point_type.title}`"
            v-for="(point_type, index) in available_point_types"
            class="individual-point-selection flex middle-xs"
            :class="{ 'selected-description': untruncated_index === index }"
          >
            <pl-checkbox
              v-model="selected_points"
              :value="point_type.tag"
              class="col-xs-1"
            />
            <div class="no-padding point-type-text col-xs-3">
              {{ point_type.title | truncate(25) }}
            </div>
            <div
              @click="
                untruncated_index = untruncated_index === index ? -1 : index
              "
              class="clickable"
              :class="[
                { 'truncate-text': untruncated_index !== index },
                point_type.is_custom ? 'col-xs-7' : 'col-xs-8',
              ]"
            >
              {{ formatPointDescription(point_type) }}
            </div>
            <div v-if="point_type.is_custom" class="clickable col-xs-1">
              <pl-menu :menuSize="2" direction="right">
                <pl-button
                  slot="menu_trigger"
                  class="pl-icon"
                  style="margin: 0"
                >
                  <i class="fas fa-ellipsis-v" />
                </pl-button>
                <ul menu-content>
                  <li @click="showPointCreationModal(point_type)">
                    <span>Edit</span>
                  </li>
                  <li @click="deletePoint(point_type)">
                    <span>Delete</span>
                  </li>
                </ul>
              </pl-menu>
            </div>
          </div>
        </div>
      </div>

      <!-- Create New Point Button -->
      <div
        @click="showPointCreationModal"
        class="new-point-button clickable row center-xs middle-xs no-padding"
      >
        <i class="fa fa-plus" /> Create Assessment Point
      </div>
    </div>

    <!-- Save Changes - Button Row -->
    <span class="sticky-button-row row middle-xs between-xs no-margin">
      <span class="col-xs-8">
        <span v-if="assessment_status">
          <span v-if="activityType === 'written'">
            You have Selected {{ selected_points.length }} assessment types
          </span>
        </span>
      </span>
      <pl-button
        v-if="user_has_changed_assessment_information"
        class="pl-primary"
        :disabled="activityType === 'verbal'"
        @click.native="updateAssessmentInformation"
      >
        Save Changes
      </pl-button>
      <pl-button @click.native="$emit('closeTab')" v-else> Close </pl-button>
    </span>

    <!-- Modals -->
    <!-- Point Creation Modal -->
    <!-- <create-new-point-modal
      :activityType="activityType"
      v-if="show_point_creation_modal"
      @close="show_point_creation_modal = false"
    /> -->
  </div>
</template>

<script>
  // Modals
  import createNewPointModal from './createNewPointModal.vue';
  import { mapGetters } from 'vuex';
  import filter from 'lodash/filter';
  import uniqBy from 'lodash/uniqBy';
  import find from 'lodash/find';
  import isEmpty from 'lodash/isEmpty';
  import xor from 'lodash/xor';
  import { EventBus } from '../utils/event-bus';

  export default {
    data() {
      return {
        untruncated_index: -1,
        show_point_creation_modal: false,
        // Status and customization
        selected_points: [],
        point_scale: 0,
        assessment_status: false,
        // Text for tooltips
        scale_description:
          'Use this number to identify a maximum value for each criteria in student assessments.',
      };
    },
    props: {
      activityType: {
        type: String,
        default: 'written',
      },
    },
    watch: {
      getCurrentRT() {
        this.selected_points = this.currentActivity.points_settings.peer_rewards
          .filter((point) => point)
          .map((point) => point.tag);
      },
    },
    computed: {
      ...mapGetters(['getCurrentRT']),
      assessment_description() {
        const points =
          'Teachers and students can acknowledge \n' +
          'true greatness with Assessment Criteria';
        const scale =
          this.activityType === 'written'
            ? '<br /> <br /> You can even control the maximum \n' +
              'number of points a student can receive at a time'
            : '';
        return points + scale;
      },
      // this allows functions to be returned dynamically based on the type of
      // discussion that is being had: verbal or written
      currentActivity() {
        if (this.activityType === 'written') {
          return this.getCurrentRT;
        } else if (this.activityType === 'verbal') {
          return this.getCurrentDiscussion;
        }
        return {};
      },
      // Get the full information for all selected points based on the tag
      full_point_settings() {
        return this.selected_points.map((tag) =>
          find(this.available_point_types, (point) => point.tag === tag)
        );
      },
      available_point_types() {
        const coursePoints = filter(
          this.getCourseDetail.points_settings.peer_rewards,
          (reward) => {
            const { tag } = reward;
            const prefix = this.activityType === 'written' ? 'rt' : 'ld';
            return (
              tag.substring(0, 2) === prefix || tag.substring(0, 3) === 'all'
            );
          }
        );
        // Displays the point type if it's in the course detail OR in the RT
        return uniqBy(
          [
            ...coursePoints,
            ...this.currentActivity.points_settings.peer_rewards,
          ],
          'tag'
        ).filter((point) => point);
      },
      user_has_changed_assessment_information() {
        const activity = this.currentActivity;
        return (
          this.assessment_status !== activity.enable_grading ||
          this.point_scale !== activity.point_cap ||
          this.have_points_changed
        );
      },
      // Used to display the save button
      have_points_changed() {
        const current_rt_point_tags =
          this.currentActivity.points_settings.peer_rewards
            .filter((point) => point)
            .map((point) => point.tag);
        return !isEmpty(xor(this.selected_points, current_rt_point_tags));
      },
      ...mapGetters([
        'getCurrentRT',
        'getCourseDetail',
        'getCurrentDiscussion',
      ]),
    },
    methods: {
      inputed() {
        if (this.point_scale === '.') {
          this.point_scale = '';
        }
        if (this.point_scale === '' || this.point_scale < 0) {
          this.point_scale = '0';
        }
        if (isNaN(this.point_scale)) {
          this.point_scale = this.point_scale.slice(0, -1);
        } else {
          this.point_scale = this.point_scale.trim();
        }
      },
      lowerPoint() {
        if (this.point_scale === '') {
          this.point_scale = '0';
          return;
        }
        this.point_scale = Number.parseFloat(this.point_scale);
        if (this.point_scale < 1) {
          this.point_scale = '0';
          return;
        }
        this.point_scale = String(this.point_scale);
        let decimal_places = 0;
        if (this.point_scale.split('.')[1])
          decimal_places = this.point_scale.split('.')[1].length;
        this.point_scale = Number.parseFloat(this.point_scale);
        this.point_scale -= 1;
        this.point_scale = String(this.point_scale.toFixed(decimal_places));
      },
      // This action updates the roundtable
      updateAssessmentInformation() {
        // Use analytics if online roundTable
        if (this.activityType === 'written') {
          if (this.getCurrentRT.point_settings !== this.full_point_settings) {
            this.$store.dispatch(
              'analytics',
              'edit written roundtable - change point settings'
            );
          }
          if (this.getCurrentRT.enable_grading !== this.assessment_status) {
            this.$store.dispatch(
              'analytics',
              'edit written roundtable - change assessment_status'
            );
          }
          if (this.getCurrentRT.point_cap !== this.point_scale) {
            this.$store.dispatch(
              'analytics',
              'edit written roundtable - change point scale'
            );
          }
        }
        // Dynamically fire actions
        const actionName =
          this.activityType === 'written'
            ? 'savingRTDetails'
            : 'saveLiveDiscussionSettings';
        this.$store.dispatch(actionName, {
          points_settings: this.full_point_settings,
          enable_grading: this.assessment_status,
          point_cap: this.point_scale,
          id: this.currentActivity.id,
        });
      },
      formatPointDescription(point) {
        const { description } = point;
        // Handle scenario where description can be an array or a string
        return Array.isArray(description)
          ? description.join('. ')
          : description;
      },
      toggleCriteriaScale(event) {
        this.point_scale = event.value
          ? this.getCurrentRT.point_cap || 4
          : null;
      },
      addPoint() {
        if (this.point_scale === '') {
          this.point_scale = 1;
          return;
        }
        let decimal_places = 0;
        this.point_scale = String(this.point_scale);
        if (this.point_scale.split('.')[1])
          decimal_places = this.point_scale.split('.')[1].length;
        this.point_scale = Number.parseFloat(this.point_scale);
        this.point_scale += 1;
        if (this.point_scale > this.pointCap) {
          this.point_scale = this.pointCap;
        }
        this.point_scale = String(this.point_scale.toFixed(decimal_places));
      },
      showPointCreationModal(point) {
        const tag = point ? point.tag : null;
        EventBus.$emit('showPointCreationModal', this.activityType, tag);
      },
      deletePoint(point) {
        const tag = point ? point.tag : null;
        EventBus.$emit('delete-assessment-point', tag);
      },
    },
    components: {
      createNewPointModal,
    },
    mounted() {
      // Set up the local state according to the current RT state
      const activity = this.currentActivity;
      this.assessment_status = activity.enable_grading;
      this.point_scale = activity.point_cap;
      this.selected_points = activity.points_settings.peer_rewards
        .filter((point) => point)
        .map((point) => point.tag);
    },
  };
</script>

<style scoped lang="stylus">
  @import '~app/global-stylus'
  // since live assessment is inside a smaller box, the font size is 90% its usual

  .live-font
    font-size: 90%;

  .warning
    color: pl-ruby

  button.pl-icon
    margin: 0 8px
    padding: 0px !important

  .selected-description
    background: alpha(pl-primary, 0.2)
    padding: 10px
    border-radius: 10px

  .chevron
    font-size: 12px

  .sticky-button-row {
    border-top: 2px solid pl-fog;
    position: sticky;
    // For safari
    position: -webkit-sticky;
    bottom: 0;
    background-color: white;
    padding: 10px 0px
  }
  .fa-info-circle {
    font-size: 12px;
  }
  .selection-container {
    margin-top: 15px;
  }
  .scale-count {
    text-align: center;
    height: 20px;
    width: 20px;
    border: 1px solid #979797;
    border-radius: 2px;
  }
  .individual-point-selection {
    padding: 1em;
    overflow-wrap: break-word;
  }
  .fa-plus {
    margin-right: 5px;
  }
  .radio-button-container {
    margin-left: 20px;
    margin-bottom: 15px;
  }
  .new-point-button {
    font-size: 16px;
    color: #808284;
    margin: 10px 0px;
    width: 100%;
    height: 40px;
    box-sizing: border-box;
    border: 1px solid #ECECEC;
    border-radius: 4px;
  }

  .point-selection-container {
    margin-top: 20px !important;
  }

  .hide
    opacity: 0.5
    pointer-events: none
</style>



// WEBPACK FOOTER //
// assessmentSettingsTab.vue?7ed65402